import { useEffect, useState } from 'react';
import {Loader2, Sparkles, Save, Trash2, Eye, EyeOff, Phone, Mail, BadgeCheck, Clock, RefreshCw, Upload, FileCheck2, CheckCircle2, AlertTriangle} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useAvailableCandidates,
  formatProfileLabel,
  CANDIDATE_QUALIFICATIONS,
  CANDIDATE_SPECIALTIES,
  SPECIALTY_LABELS,
  WORK_ENVIRONMENTS,
  EXPERIENCE_LEVELS,
  FRENCH_REGIONS,
  FRENCH_DEPARTMENTS,
  MOBILITY_RADIUS_OPTIONS,
  formatRemaining,
  formatPeriodRange,
  type CandidateQualification,
  type CandidateSpecialty,
  type CandidateWorkEnvironment,
  type CandidateExperience,
  type CandidateMobility,
  type CandidateHousingNeed,
  type MobilityRadiusKm,
  type AvailableCandidate,
  type AvailabilityMode } from
'@/hooks/useAvailableCandidates';
import { fetchProDossierByCreator } from '@/hooks/useProDossier';
import { uploadOneFile } from '@/lib/upload';
import { emailCandidate_AvailabilityExpired } from '@/lib/emails';
import { lumi } from '@/lib/lumi';

interface Props {
  userId: string;
  email?: string;
}

export default function AvailabilityProfileForm({ userId, email }: Props) {
  const { fetchMine, upsert, removeMine, reactivate, markExpired, saving, error } =
  useAvailableCandidates();

  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState<AvailableCandidate | null>(null);

  // Statut du dossier auto-entrepreneur (pour proposer ou non le téléversement
  // direct du diplôme dans ce formulaire).
  type AeStatus = 'none' | 'pending' | 'approved' | 'rejected';
  const [aeStatus, setAeStatus] = useState<AeStatus>('none');
  const [diplomaUrl, setDiplomaUrl] = useState<string>('');
  const [diplomaUploading, setDiplomaUploading] = useState(false);

  // Form state
  const [qualification, setQualification] = useState<CandidateQualification>('BNSSA');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState<string>('');
  const [specialty, setSpecialty] = useState<CandidateSpecialty | ''>('');
  const [workEnvironment, setWorkEnvironment] = useState<CandidateWorkEnvironment | ''>('');
  const [experience, setExperience] = useState<CandidateExperience | ''>('');
  const [mobility, setMobility] = useState<CandidateMobility>('non');
  const [mobilityRadiusKm, setMobilityRadiusKm] = useState<MobilityRadiusKm>(20);
  const [mobilityRegion, setMobilityRegion] = useState<string>('');
  const [mobilityDepartment, setMobilityDepartment] = useState<string>('');
  const [housingNeed, setHousingNeed] = useState<CandidateHousingNeed>('non');
  const [phone, setPhone] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>(email || '');
  const [isPublished, setIsPublished] = useState(true);
  const [isAvailableNow, setIsAvailableNow] = useState<boolean>(false);
  // Nouveau : mode de disponibilité (immédiat 48h vs période datée)
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>('immediate');
  const [periodStart, setPeriodStart] = useState<string>('');
  const [periodEnd, setPeriodEnd] = useState<string>('');

  // Tick pour rafraîchir le compte à rebours toutes les 60s
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Préremplit le formulaire avec le profil existant
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
    fetchMine(userId),
    fetchProDossierByCreator(userId, email)]
    ).
    then(async ([d, dossier]) => {
      if (cancelled) return;
      // Statut du dossier AE
      const status = (dossier as {validationStatus?: AeStatus;} | null)?.validationStatus;
      if (status === 'approved' || status === 'pending' || status === 'rejected') {
        setAeStatus(status);
      } else if (dossier) {
        setAeStatus('pending');
      } else {
        setAeStatus('none');
      }

      if (d) {
        setExisting(d);
        setQualification(d.qualification || 'BNSSA');
        setCity(d.city || '');
        setRegion(d.region || '');
        setSpecialty((d.specialty as CandidateSpecialty) || '');
        setWorkEnvironment((d.workEnvironment as CandidateWorkEnvironment) || '');
        setExperience((d.experience as CandidateExperience) || '');
        setMobility(d.mobility || 'non');
        if (d.mobilityRadiusKm) setMobilityRadiusKm(d.mobilityRadiusKm as MobilityRadiusKm);
        if (d.mobilityRegion) setMobilityRegion(d.mobilityRegion);
        if (d.mobilityDepartment) setMobilityDepartment(d.mobilityDepartment);
        setHousingNeed(d.housingNeed || 'non');
        setPhone(d.phone || '');
        setContactEmail(d.email || email || '');
        setIsPublished(d.isPublished !== false);
        setIsAvailableNow(d.isAvailableNow === true);
        setAvailabilityMode(d.availabilityMode === 'period' ? 'period' : 'immediate');
        setPeriodStart(d.periodStart ? d.periodStart.slice(0, 10) : '');
        setPeriodEnd(d.periodEnd ? d.periodEnd.slice(0, 10) : '');
        setDiplomaUrl(d.diplomaUrl || '');

        // Auto-expiration : si la fenêtre 48h est dépassée et que le profil
        // est encore publié, on bascule isPublished=false et on envoie un
        // email de réactivation (une seule fois grâce à expirationEmailSentAt).
        const expired =
        d.availabilityMode !== 'period' &&
        !!d.availableUntil && new Date(d.availableUntil).getTime() < Date.now();
        if (expired && d.isPublished !== false && d._id) {
          await markExpired(d._id);
          setIsPublished(false);
          const target = d.email || d.creatorEmail || email;
          if (target && !d.expirationEmailSentAt) {
            try {
              await emailCandidate_AvailabilityExpired({
                candidateEmail: target,
                qualification: d.qualification,
                publicId: d.publicId
              });
            } catch (err) {
              console.warn('[AvailabilityProfileForm] expired email failed', err);
            }
          }
        }
      }
    }).
    finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, fetchMine, markExpired, email]);

  const expired =
  !!existing?.availableUntil &&
  new Date(existing.availableUntil).getTime() < Date.now();

  const remaining = existing?.availableUntil ? formatRemaining(existing.availableUntil) : '';
  const profileLabel = existing?.publicId ?
  formatProfileLabel(existing.qualification, existing.publicId) :
  `${qualification}-—`;

  // Le candidat doit-il pouvoir téléverser un diplôme directement ici ?
  // À partir du moment où son dossier AE n'est pas "approved".
  const canShowDiplomaUploader = aeStatus !== 'approved';
  const hasVerifiedDiploma = aeStatus === 'approved' || !!diplomaUrl;

  const handleDiplomaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (10 Mo max).');
      return;
    }
    setDiplomaUploading(true);
    try {
      const url = await uploadOneFile(file);
      if (!url) {
        toast.error('Échec du téléversement, réessayez.');
        return;
      }
      setDiplomaUrl(url);
      toast.success('Diplôme téléversé — pensez à enregistrer votre profil.');
    } catch (err) {
      console.error('[AvailabilityProfileForm] diploma upload failed', err);
      toast.error('Échec du téléversement.');
    } finally {
      setDiplomaUploading(false);
    }
  };

  const handleDiplomaRemove = () => {
    setDiplomaUrl('');
    toast('Diplôme retiré. Pensez à enregistrer votre profil.', { icon: 'i' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!city.trim()) {
      toast.error('Veuillez renseigner votre ville.');
      return;
    }
    if (!region.trim()) {
      toast.error('Veuillez sélectionner votre région.');
      return;
    }
    if (!phone.trim()) {
      toast.error('Veuillez renseigner votre téléphone.');
      return;
    }
    if (!contactEmail.trim() || !/^\S+@\S+\.\S+$/.test(contactEmail.trim())) {
      toast.error('Veuillez renseigner un email de contact valide.');
      return;
    }
    if (mobility === 'locale' && !mobilityRadiusKm) {
      toast.error('Veuillez préciser le rayon de mobilité (km).');
      return;
    }
    if (mobility === 'departementale' && !mobilityDepartment.trim()) {
      toast.error('Veuillez sélectionner votre département de mobilité.');
      return;
    }
    if (mobility === 'regionale' && !mobilityRegion.trim()) {
      toast.error('Veuillez sélectionner la région souhaitée.');
      return;
    }
    if (availabilityMode === 'period') {
      if (!periodStart || !periodEnd) {
        toast.error('Veuillez renseigner les dates de début et de fin de disponibilité.');
        return;
      }
      if (new Date(periodEnd).getTime() < new Date(periodStart).getTime()) {
        toast.error('La date de fin doit être postérieure à la date de début.');
        return;
      }
    }

    try {
      const saved = await upsert(
        {
          qualification,
          city,
          region,
          specialty: specialty || undefined,
          workEnvironment: workEnvironment || undefined,
          experience: experience || undefined,
          mobility,
          mobilityRadiusKm: mobility === 'locale' ? mobilityRadiusKm : undefined,
          mobilityRegion: mobility === 'regionale' ? mobilityRegion : undefined,
          mobilityDepartment: mobility === 'departementale' ? mobilityDepartment : undefined,
          housingNeed,
          phone,
          email: contactEmail,
          isPublished,
          // garde-fous côté UI (le hook re-filtre aussi) : BNSSA sans spécialité, MNS sans milieu
          // (les valeurs effectives sont ré-arbitrées dans upsert selon la qualification).
          isAvailableNow: availabilityMode === 'immediate' ? isAvailableNow : false,
          availabilityMode,
          periodStart: availabilityMode === 'period' ? periodStart : undefined,
          periodEnd: availabilityMode === 'period' ? periodEnd : undefined,
          creatorEmail: email,
          diplomaUrl: aeStatus === 'approved' ? null : diplomaUrl || null
        },
        userId
      );
      setExisting(saved);

      // Notifier en best-effort les recruteurs dont une alerte candidat
      // correspond à ce profil fraîchement publié (dispo immédiate 48h).
      // On délègue à la fonction Deno serveur `notifyCandidatePublished` : la
      // collection `candidate_alerts` est en read:CREATOR_ONLY, donc le candidat
      // ne peut PAS lister les alertes recruteurs côté client. Le serveur, lui,
      // utilise LUMI_API_KEY et contourne cette restriction.
      // Indépendant de toute connexion recruteur ou du cron serveur.
      if (saved && saved._id && saved.isPublished !== false && saved.isAvailableNow === true) {
        void lumi.functions
          .invoke('notifyCandidatePublished', {
            method: 'POST',
            body: { candidateId: saved._id }
          })
          .catch((err: unknown) =>
          console.warn('[AvailabilityProfileForm] candidate alerts notify failed (non bloquant):', err)
          );
      }

      if (!isPublished) {
        toast.success('Profil enregistré (non publié).');
      } else if (availabilityMode === 'period') {
        toast.success('Profil publié sur la période choisie dans la CVthèque !');
      } else if (isAvailableNow) {
        toast.success('Profil publié pour 48h dans la CVthèque !');
      } else {
        toast.success('Profil enregistré (non publié).');
      }
    } catch {
      toast.error("Impossible d'enregistrer votre profil.");
    }
  };

  const handleReactivate = async () => {
    try {
      const updated = await reactivate(userId);
      if (updated) {
        setExisting(updated);
        setIsPublished(true);
        setIsAvailableNow(true);
        toast.success('Profil réactivé pour 48h !');
      }
    } catch {
      toast.error('Impossible de réactiver votre profil.');
    }
  };

  const handleRemove = async () => {
    if (!existing) return;
    if (!confirm('Retirer votre profil de la CVthèque ?')) return;
    try {
      await removeMine(userId);
      setExisting(null);
      setIsPublished(false);
      setIsAvailableNow(false);
      toast.success('Profil retiré de la CVthèque.');
    } catch {
      toast.error('Échec de la suppression.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement de votre profil…
        </div>
      </div>);

  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-5 sm:p-6 bg-gradient-to-r from-amber-50 via-white to-blue-50 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Mes disponibilités !

            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Apparaissez de manière <strong>anonyme</strong> dans la CVthèque{' '}
              <span className="font-semibold">« Ils sont disponibles ! »</span>{' '}
              pendant 48&nbsp;heures, ou plus tard sous votre identifiant{' '}
              <span className="inline-flex items-center gap-1 font-mono text-[12px] bg-gray-900 text-white px-2 py-0.5 rounded">
                <BadgeCheck className="w-3 h-3" />
                {profileLabel}
              </span>
              .
            </p>
            {existing &&
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <span
                className={`inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-2 py-0.5 border ${
                isPublished && isAvailableNow && !expired ?
                'bg-emerald-50 text-emerald-700 border-emerald-200' :
                'bg-gray-100 text-gray-600 border-gray-200'}`
                }>

                  {isPublished && isAvailableNow && !expired ?
                <>
                      <Eye className="w-3 h-3" /> En ligne
                    </> :

                <>
                      <EyeOff className="w-3 h-3" /> Hors ligne
                    </>
                }
                </span>
                {existing.availableUntil &&
              <span
                className={`inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-2 py-0.5 border ${
                expired ?
                'bg-rose-50 text-rose-700 border-rose-200' :
                'bg-blue-50 text-blue-700 border-blue-200'}`
                }>

                    <Clock className="w-3 h-3" />
                    {expired ? 'Expiré (48h écoulées)' : remaining}
                  </span>
              }
              </div>
            }
          </div>
        </div>

        {existing && expired &&
        <div className="mt-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-rose-900 flex-1">
              Votre profil a été automatiquement retiré de la CVthèque après 48&nbsp;heures.
              Vous pouvez le <strong>réactiver pour 48h</strong> en un clic.
            </p>
            <button
            type="button"
            onClick={handleReactivate}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">

              <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
              Réactiver pour 48h
            </button>
          </div>
        }
      </div>

      <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
        {/* Mode de disponibilité : Immédiat (48h) vs Période datée — placé en haut du formulaire */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Type de disponibilité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
            {
              value: 'immediate' as AvailabilityMode,
              title: 'Disponible immédiatement',
              desc: 'Visible 48h, renouvelable'
            },
            {
              value: 'period' as AvailabilityMode,
              title: 'Disponible sur une période',
              desc: 'Du / au — dates précises'
            }].
            map((opt) => {
              const active = availabilityMode === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer text-sm transition-colors ${
                  active ?
                  'border-blue-500 bg-blue-50 text-blue-900' :
                  'border-gray-200 hover:border-gray-300 text-gray-700'}`
                  }>

                  <input
                    type="radio"
                    name="availabilityMode"
                    value={opt.value}
                    checked={active}
                    onChange={() => setAvailabilityMode(opt.value)}
                    className="accent-blue-600 mt-0.5" />

                  <span className="min-w-0">
                    <span className="block font-semibold">{opt.title}</span>
                    <span className="block text-[11px] text-gray-500">{opt.desc}</span>
                  </span>
                </label>);

            })}
          </div>
        </div>

        {/* Mode période : dates de début et de fin — placé juste sous le choix du mode */}
        {availabilityMode === 'period' &&
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <label className="block text-xs font-semibold text-blue-900 mb-2">
              Période de disponibilité <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="block text-[11px] font-semibold text-blue-900/80 mb-1">Du</span>
                <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />

              </div>
              <div>
                <span className="block text-[11px] font-semibold text-blue-900/80 mb-1">Au</span>
                <input
                type="date"
                value={periodEnd}
                min={periodStart || undefined}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />

              </div>
            </div>
            {periodStart && periodEnd &&
          <p className="text-[11px] text-blue-900/80 mt-2">
                Vous apparaîtrez dans la CVthèque comme disponible{' '}
                <strong>{formatPeriodRange(periodStart, periodEnd)}</strong>. Aucune expiration 48h en mode période.
              </p>
          }
          </div>
        }

        {/* Qualification */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Vous êtes <span className="text-red-500">*</span>{' '}
            <span className="text-[10px] font-normal text-emerald-700 uppercase tracking-wide">
              Public
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {CANDIDATE_QUALIFICATIONS.map((q) => {
              const active = qualification === q;
              return (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQualification(q)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  active ?
                  'bg-gray-900 text-white border-gray-900' :
                  'bg-white text-gray-700 border-gray-200 hover:border-gray-300'}`
                  }>

                  {q}
                </button>);

            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            En CVthèque publique vous apparaissez sous la forme{' '}
            <span className="font-mono text-gray-700">{profileLabel}</span>.
          </p>
        </div>

        {/* Ville + Région */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Votre ville <span className="text-red-500">*</span>{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
              placeholder="Ex. Marseille, Lyon, Bordeaux…"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

            <p className="text-[11px] text-gray-400 mt-1">
              Visible uniquement par les recruteurs connectés.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Votre région <span className="text-red-500">*</span>{' '}
              <span className="text-[10px] font-normal text-emerald-700 uppercase tracking-wide">
                Public
              </span>
            </label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

              <option value="">— Sélectionner une région —</option>
              {FRENCH_REGIONS.map((r) =>
              <option key={r} value={r}>{r}</option>
              )}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">
              Affichée sur la CVthèque publique.
            </p>
          </div>
        </div>

        {/* Spécialité (MNS) OU Milieu (BNSSA) + Expérience (privés, grisés tant que recruteur non connecté) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* BNSSA = surveillance uniquement → pas de spécialité, on demande le milieu. */}
          {qualification === 'BNSSA' ?
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Milieu de travail{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <select
              value={workEnvironment}
              onChange={(e) => setWorkEnvironment(e.target.value as CandidateWorkEnvironment | '')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

              <option value="">— Non précisé —</option>
              {WORK_ENVIRONMENTS.map((w) =>
              <option key={w} value={w}>{w}</option>
              )}
            </select>
          </div> :
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Spécialité{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value as CandidateSpecialty | '')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

              <option value="">— Non précisée —</option>
              {CANDIDATE_SPECIALTIES.map((s) =>
              <option key={s} value={s}>{SPECIALTY_LABELS[s]}</option>
              )}
            </select>
          </div>
          }
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Expérience{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <select
              value={experience}
              onChange={(e) => setExperience(e.target.value as CandidateExperience | '')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

              <option value="">— Non précisée —</option>
              {EXPERIENCE_LEVELS.map((x) =>
              <option key={x} value={x}>{x}</option>
              )}
            </select>
          </div>
        </div>

        {/* Téléphone + Email (privés) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Téléphone <span className="text-red-500">*</span>{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <div className="relative">
              <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="06 12 34 56 78"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Caché en public, communiqué uniquement aux recruteurs connectés.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Email de contact <span className="text-red-500">*</span>{' '}
              <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                Privé
              </span>
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
                placeholder="vous@exemple.com"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />

            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Caché en public, communiqué uniquement aux recruteurs connectés.
            </p>
          </div>
        </div>

        {/* Bandeau confidentialité */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900">
          <p className="font-semibold mb-1">Confidentialité de votre profil public</p>
          <p>
            Aucun nom et aucun prénom ne sont publiés. Vous apparaissez sous{' '}
            <span className="font-mono">{profileLabel}</span>, avec votre <strong>région</strong>,
            votre qualification, votre mobilité, votre besoin en logement et la mention « Disponible
            maintenant&nbsp;! ». Vos coordonnées (téléphone, email) ne sont visibles que par les{' '}
            <strong>recruteurs connectés</strong>.
          </p>
        </div>

        {/* Mobilité */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Mobilité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
            { value: 'non', label: 'Non' },
            { value: 'locale', label: 'Oui, localement' },
            { value: 'departementale', label: 'Oui, dans mon département' },
            { value: 'regionale', label: 'Oui, régionalement' },
            { value: 'france', label: 'Oui, partout en France' }].
            map((opt) => {
              const active = mobility === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  active ?
                  'border-blue-500 bg-blue-50 text-blue-800' :
                  'border-gray-200 hover:border-gray-300 text-gray-700'}`
                  }>

                  <input
                    type="radio"
                    name="mobility"
                    value={opt.value}
                    checked={active}
                    onChange={() => setMobility(opt.value as CandidateMobility)}
                    className="accent-blue-600" />

                  {opt.label}
                </label>);

            })}
          </div>

          {mobility === 'locale' &&
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Dans un rayon de combien de km ? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {MOBILITY_RADIUS_OPTIONS.map((km) => {
                const active = mobilityRadiusKm === km;
                return (
                  <button
                    key={km}
                    type="button"
                    onClick={() => setMobilityRadiusKm(km)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    active ?
                    'bg-blue-600 text-white border-blue-600' :
                    'bg-white text-blue-800 border-blue-200 hover:border-blue-400'}`
                    }>

                      {km} km
                    </button>);

              })}
              </div>
            </div>
          }

          {mobility === 'departementale' &&
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Département de mobilité <span className="text-red-500">*</span>{' '}
                <span className="text-[10px] font-normal text-amber-700 uppercase tracking-wide">
                  Privé
                </span>
              </label>
              <select
              value={mobilityDepartment}
              onChange={(e) => setMobilityDepartment(e.target.value)}
              required
              className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

                <option value="">— Sélectionner un département —</option>
                {FRENCH_DEPARTMENTS.map((d) =>
              <option key={d} value={d}>{d}</option>
              )}
              </select>
              <p className="text-[11px] text-blue-900/70 mt-2">
                Le département reste <strong>masqué en CVthèque publique</strong> ; seuls les recruteurs connectés y ont accès. Sur la fiche publique, seule votre <strong>région</strong> sera affichée.
              </p>
            </div>
          }

          {mobility === 'regionale' &&
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Région souhaitée <span className="text-red-500">*</span>
              </label>
              <select
              value={mobilityRegion}
              onChange={(e) => setMobilityRegion(e.target.value)}
              required
              className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">

                <option value="">— Sélectionner une région —</option>
                {FRENCH_REGIONS.map((r) =>
              <option key={r} value={r}>{r}</option>
              )}
              </select>
            </div>
          }
        </div>

        {/* Logement */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Besoin d'un logement <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
            { value: 'non', label: 'Non' },
            { value: 'oui', label: 'Oui' },
            { value: 'oui_si_eloigne', label: 'Oui si mission éloignée' }].
            map((opt) => {
              const active = housingNeed === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  active ?
                  'border-amber-500 bg-amber-50 text-amber-800' :
                  'border-gray-200 hover:border-gray-300 text-gray-700'}`
                  }>

                  <input
                    type="radio"
                    name="housingNeed"
                    value={opt.value}
                    checked={active}
                    onChange={() => setHousingNeed(opt.value as CandidateHousingNeed)}
                    className="accent-amber-600" />

                  {opt.label}
                </label>);

            })}
          </div>
        </div>

        {/* Bloc diplôme vérifié — téléversement direct si dossier AE non validé */}
        {canShowDiplomaUploader &&
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                {hasVerifiedDiploma ?
              <FileCheck2 className="w-5 h-5 text-emerald-600" /> :

              <AlertTriangle className="w-5 h-5 text-amber-600" />
              }
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900">
                  Diplôme vérifié
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {aeStatus === 'pending' &&
                <>Votre dossier auto-entrepreneur est en attente de validation. En téléversant votre <strong>diplôme principal</strong> ici, vous obtenez immédiatement le badge <strong>« Diplôme vérifié »</strong> sur la CVthèque.</>
                }
                  {aeStatus === 'rejected' &&
                <>Votre dossier auto-entrepreneur a été refusé. Téléversez votre <strong>diplôme principal</strong> pour afficher le badge <strong>« Diplôme vérifié »</strong> en attendant la mise à jour de votre dossier.</>
                }
                  {aeStatus === 'none' &&
                <>Vous n'avez pas encore de dossier auto-entrepreneur. Téléversez votre <strong>diplôme principal</strong> pour afficher le badge <strong>« Diplôme vérifié »</strong> sur la CVthèque en attendant.</>
                }
                </p>

                {diplomaUrl ?
              <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Diplôme vérifié
                    </span>
                    <a
                  href={diplomaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-gray-700 hover:text-gray-900 break-all">

                      Voir le fichier
                    </a>
                    <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-800 cursor-pointer">
                      <input
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={handleDiplomaUpload}
                    disabled={diplomaUploading}
                    className="hidden" />

                      <Upload className="w-3.5 h-3.5" />
                      Remplacer
                    </label>
                    <button
                  type="button"
                  onClick={handleDiplomaRemove}
                  className="text-xs font-semibold text-rose-600 hover:text-rose-700">

                      Retirer
                    </button>
                  </div> :

              <label
                className={`mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed text-xs font-semibold cursor-pointer transition-colors ${
                diplomaUploading ?
                'border-gray-200 bg-gray-50 text-gray-400 cursor-wait' :
                'border-amber-300 bg-white text-amber-800 hover:border-amber-400 hover:bg-amber-50'}`
                }>

                    <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleDiplomaUpload}
                  disabled={diplomaUploading}
                  className="hidden" />

                    {diplomaUploading ?
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> :

                <Upload className="w-3.5 h-3.5" />
                }
                    {diplomaUploading ?
                'Téléversement…' :
                'Téléverser mon diplôme principal (PDF ou image)'}
                  </label>
              }
                <p className="text-[11px] text-gray-400 mt-2">
                  Format accepté : PDF, JPG, PNG. Taille max&nbsp;: 10 Mo. Visible uniquement par les administrateurs.
                </p>
              </div>
            </div>
          </div>
        }

        {/* Mode immédiat : toggle Oui/Non (déclenche la fenêtre 48h) */}
        {availabilityMode === 'immediate' &&
        <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Disponible Immédiatement&nbsp;? <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 max-w-md">
              {[
            { value: true, label: 'Oui' },
            { value: false, label: 'Non' }].
            map((opt) => {
              const active = isAvailableNow === opt.value;
              return (
                <label
                  key={String(opt.value)}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm font-semibold transition-colors ${
                  active ?
                  opt.value ?
                  'border-emerald-500 bg-emerald-50 text-emerald-800' :
                  'border-gray-400 bg-gray-100 text-gray-700' :
                  'border-gray-200 hover:border-gray-300 text-gray-700'}`
                  }>

                    <input
                    type="radio"
                    name="isAvailableNow"
                    checked={active}
                    onChange={() => setIsAvailableNow(opt.value)}
                    className={opt.value ? 'accent-emerald-600' : 'accent-gray-500'} />

                    {opt.label}
                  </label>);

            })}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              Si « Oui », votre profil reste visible <strong>48 heures</strong> dans la CVthèque
              publique avec un compte à rebours « Encore disponible 1 j 10 h ». Vous serez prévenu(e)
              par email à la fin pour réactiver si vous le souhaitez.
            </p>
          </div>
        }

        {/* Visibilité */}
        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <input
            id="isPublished"
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
            className="w-4 h-4 accent-blue-600" />

          <label htmlFor="isPublished" className="text-sm text-gray-700 cursor-pointer">
            Publier mon profil dans la CVthèque publique
          </label>
        </div>

        {error &&
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        }

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60">

            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {existing ?
            'Mettre à jour mon profil' :
            availabilityMode === 'period' ?
            'Publier mon profil (période)' :
            'Publier mon profil (48h)'}
          </button>
          {existing && !expired && isPublished && isAvailableNow &&
          <button
            type="button"
            onClick={handleReactivate}
            disabled={saving}
            className="inline-flex items-center gap-2 border border-blue-200 text-blue-700 hover:bg-blue-50 px-4 py-2.5 rounded-xl text-sm font-semibold"
            title="Repartir pour 48h">

              <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
              Réactiver pour 48h
            </button>
          }
          {existing &&
          <button
            type="button"
            onClick={handleRemove}
            className="inline-flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2.5 rounded-xl text-sm font-semibold">

              <Trash2 className="w-4 h-4" />
              Retirer de la CVthèque
            </button>
          }
        </div>
      </form>
    </div>);

}